/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { isLocal } from '@squiz/dxp-console-support';
import { IdpLoginRequest, IdpSettingsMissing, SamlLoginRequest } from '@squiz/dxp-identity-gateway-types';
import { BaseService } from 'apps/console-core/src/Services/_base';
import { HttpResponse } from 'apps/console-core/src/Services/http';
import { Observable, switchMap } from 'rxjs';
import { IdentityGatewayServiceConfiguration } from './IdentityGateway.service.config';

/**
 * The identity gateway service.
 */
export class IdentityGatewayService extends BaseService {
  public constructor(configuration: IdentityGatewayServiceConfiguration) {
    super(configuration);
  }

  /**
   * Creates base api url for the service.
   */
  getBaseServiceUrl(): string {
    if (isLocal()) {
      return `https://${process.env.DXP_CONSOLE_DOMAIN}/__sso`;
    }

    return `/__sso`;
  }

  /**
   * Creates IdP login url.
   */
  public createIdpLoginUrl(): string {
    const baseUrl = this.getBaseServiceUrl();

    return `${baseUrl}/console/login`;
  }

  /**
   * Creates SSO callback url.
   */
  public createSsoCallbackUrl(): string {
    const baseUrl = this.getBaseServiceUrl();

    return `${baseUrl}/callback`;
  }

  /**
   * Creates SSO log out url.
   */
  public createSsoLogoutUrl(): string {
    const baseUrl = this.getBaseServiceUrl();

    return `${baseUrl}/logout`;
  }

  /**
   * Attempt to log in to the DXP through an IdP.
   */
  public logInToIdp(dto: IdpLoginRequest): Observable<SamlLoginRequest | IdpSettingsMissing> {
    const requestUrl = this.createIdpLoginUrl();

    return this.httpService
      .post<unknown, HttpResponse>(requestUrl, {
        body: dto,
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<SamlLoginRequest | IdpSettingsMissing> => {
          return this._handleResponseError<SamlLoginRequest | IdpSettingsMissing>(value);
        }),
      );
  }

  /**
   * Attempt to log out of the DXP.
   */
  public logOutOfDxp(): Observable<null> {
    const requestUrl = this.createSsoLogoutUrl();

    return this.httpService.post<unknown, HttpResponse>(requestUrl, this.getSessionRequestOptions()).pipe(
      switchMap((value: HttpResponse): Observable<null> => {
        return this._handleResponseError<null>(value);
      }),
    );
  }
}
